import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building, Users, Copy, Check, Tag, Tags, FilePlus, Pencil, Trash } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Header from '@/components/layout/Header';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuthRedirect } from '@/hooks/useAuthRedirect';

// Mock organization data
interface Organization {
  id: string;
  name: string;
  description: string;
  membersCount: number;
  createdAt: string;
  categories: string[];
}

// Mock tag categories
const AVAILABLE_CATEGORIES = [
  'Web Development',
  'Mobile Development',
  'Data Science',
  'DevOps',
  'Design',
  'Business',
  'Marketing',
  'Project Management',
  'Soft Skills'
];

// Mock data for organizations
const MOCK_ORGANIZATIONS: Organization[] = [
  {
    id: '1',
    name: 'ABC Company',
    description: 'Enterprise technology solutions company specializing in cloud infrastructure and DevOps.',
    membersCount: 24,
    createdAt: '2023-05-15T10:30:00',
    categories: ['Web Development', 'DevOps', 'Data Science']
  },
  {
    id: '2',
    name: 'TechStart Academy',
    description: 'Educational institution focused on preparing students for careers in technology.',
    membersCount: 126,
    createdAt: '2023-07-10T09:15:00',
    categories: ['Web Development', 'Mobile Development', 'Design']
  },
  {
    id: '3',
    name: 'InnovateX Labs',
    description: 'Research and development firm working on cutting-edge technologies.',
    membersCount: 11,
    createdAt: '2023-08-05T16:00:00',
    categories: ['Data Science', 'Project Management']
  },
];

const OrganizationManagement: React.FC = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [newOrg, setNewOrg] = useState({
    name: '',
    description: '',
    categories: [] as string[]
  });
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [inviteKey, setInviteKey] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [orgNameError, setOrgNameError] = useState<string | null>(null);

  // Use the auth redirect hook
  useAuthRedirect(false);
  
  // Effect for admin check and data fetching
  useEffect(() => {
    if (isAuthenticated && !isAdmin) {
      navigate('/dashboard'); // Redirect non-admins
      return;
    }
    
    if (isAuthenticated && isAdmin) {
      // In a real app, fetch organizations from API here
      // Simulate API call for now
      setTimeout(() => {
        setOrganizations(MOCK_ORGANIZATIONS);
        setIsLoading(false); // Set loading false after data is "fetched"
      }, 500);
    } else if (!isAuthenticated) {
      // Hook handles redirect, set loading false
      setIsLoading(false);
    }
  }, [isAuthenticated, isAdmin, navigate]);
  
  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
        <p className="mt-4 text-muted-foreground">Loading organizations...</p>
      </div>
    );
  }
  
  // Create a new organization
  const handleCreateOrg = async () => {
    console.log("[handleCreateOrg] Attempting to create org with name:", newOrg.name);
    if (!newOrg.name.trim()) {
      toast.error('Organization name is required');
      console.log("[handleCreateOrg] Name is empty.");
      return;
    }

    const nameExists = checkOrgNameLocally(newOrg.name.trim());
    console.log("[handleCreateOrg] Result of checkOrgNameLocally:", nameExists);
    if (nameExists) {
        toast.error("Organization name already exists");
        console.log("[handleCreateOrg] Name exists, creation aborted.");
        return;
    }
    
    console.log("[handleCreateOrg] Name is unique, proceeding with creation.");
    const newOrgData: Organization = {
      id: Math.random().toString(36).substr(2, 9),
      name: newOrg.name.trim(),
      description: newOrg.description.trim(),
      membersCount: 1, // Creator is the first member
      createdAt: new Date().toISOString(),
      categories: selectedCategories
    };
    
    setOrganizations([...organizations, newOrgData]);
    setNewOrg({ name: '', description: '', categories: [] });
    setSelectedCategories([]);
    setIsCreateDialogOpen(false);
    toast.success('Organization created successfully');
  };
  
  // Update an organization
  const handleUpdateOrg = () => {
    if (!editingOrg) return;
    
    if (!editingOrg.name.trim()) {
      toast.error('Organization name is required');
      return;
    }
    
    const updatedOrgs = organizations.map(org => 
      org.id === editingOrg.id ? { ...editingOrg, categories: selectedCategories } : org
    );
    
    setOrganizations(updatedOrgs);
    setEditingOrg(null);
    setSelectedCategories([]);
    setIsEditDialogOpen(false);
    toast.success('Organization updated successfully');
  };
  
  // Start editing an organization
  const handleEditOrg = (org: Organization) => {
    setEditingOrg(org);
    setSelectedCategories(org.categories);
    setIsEditDialogOpen(true);
  };
  
  // Delete an organization
  const handleDeleteOrg = (orgId: string) => {
    if (confirm('Are you sure you want to delete this organization?')) {
      setOrganizations(organizations.filter(org => org.id !== orgId));
      toast.success('Organization deleted successfully');
    }
  };
  
  // Generate invitation key
  const handleGenerateInvite = (orgId: string) => {
    setSelectedOrgId(orgId);
    // Generate a mock invitation key
    const key = `org_invite_${Math.random().toString(36).substr(2, 16)}`;
    setInviteKey(key);
    setCopied(false);
    setIsInviteDialogOpen(true);
  };
  
  // Copy invitation key to clipboard
  const handleCopyInvite = () => {
    navigator.clipboard.writeText(inviteKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Invitation key copied to clipboard');
  };
  
  // Format date to readable string
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  
  // Category toggle handler
  const handleCategoryToggle = (category: string) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter(c => c !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
  };

  // Validates against the local organizations list
  const checkOrgNameLocally = (name: string): boolean => {
    console.log("[checkOrgNameLocally] Validating name:", name);
    if (!name.trim()) {
        setOrgNameError(null); // Clear error if name is empty
        console.log("[checkOrgNameLocally] Name is empty, clearing error.");
        return false;
    }
    const exists = organizations.some(
      (org) => org.name.toLowerCase() === name.trim().toLowerCase()
    );
    console.log("[checkOrgNameLocally] Name exists in local list:", exists);
    setOrgNameError(exists ? "Organization name already exists" : null);
    return exists;
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      
      <main className="flex-1 container py-6">
        <Header 
          title="Organization Management" 
          description="Create and manage organizations"
        >
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Organization
          </Button>
        </Header>
        
        <Tabs defaultValue="all" className="mt-6">
          <TabsList>
            <TabsTrigger value="all">All Organizations</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all" className="mt-6">
            {organizations.length === 0 ? (
              <div className="rounded-lg border border-dashed p-8 text-center">
                <Building className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-medium">No organizations found</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Start by creating your first organization.
                </p>
                <Button onClick={() => setIsCreateDialogOpen(true)} className="mt-4">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Organization
                </Button>
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {organizations.map(org => (
                  <Card key={org.id} className="flex flex-col">
                    <CardHeader>
                      <CardTitle>{org.name}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {org.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              {org.membersCount} members
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            Created {formatDate(org.createdAt)}
                          </span>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Tags className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Categories</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {org.categories.length > 0 ? (
                              org.categories.map(category => (
                                <Badge key={category} variant="outline" className="text-xs">
                                  {category}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-xs text-muted-foreground">No categories</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="flex flex-wrap gap-2 border-t pt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditOrg(org)}
                        className="flex-1"
                      >
                        <Pencil className="mr-2 h-3 w-3" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleGenerateInvite(org.id)}
                        className="flex-1"
                      >
                        <Users className="mr-2 h-3 w-3" />
                        Invite
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteOrg(org.id)}
                        className="text-destructive hover:bg-destructive/10"
                      >
                        <Trash className="h-3 w-3" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
        
        {/* Create Organization Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New Organization</DialogTitle>
              <DialogDescription>
                Add the details for your new organization.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Organization Name *</Label>
                <Input
                  id="name"
                  placeholder="Enter organization name"
                  value={newOrg.name}
                  onChange={(e) => {
                    setNewOrg({ ...newOrg, name: e.target.value });
                    setOrgNameError(null);
                  }}
                  onBlur={(e) => {
                    console.log("[onBlur] Input blurred. Value:", e.target.value.trim());
                    checkOrgNameLocally(e.target.value.trim());
                  }}
                />
                {orgNameError && (
                  <p className="text-red-500 text-xs mt-1">{orgNameError}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Enter organization description"
                  rows={3}
                  value={newOrg.description}
                  onChange={(e) => setNewOrg({ ...newOrg, description: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Categories</Label>
                <div className="grid grid-cols-2 gap-2">
                  {AVAILABLE_CATEGORIES.map(category => (
                    <div key={category} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`category-${category}`} 
                        checked={selectedCategories.includes(category)}
                        onCheckedChange={() => handleCategoryToggle(category)}
                      />
                      <label
                        htmlFor={`category-${category}`}
                        className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {category}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateOrg} disabled={!!orgNameError}>Create Organization</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Edit Organization Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Organization</DialogTitle>
              <DialogDescription>
                Update the details for this organization.
              </DialogDescription>
            </DialogHeader>
            
            {editingOrg && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Organization Name *</Label>
                  <Input
                    id="edit-name"
                    placeholder="Enter organization name"
                    value={editingOrg.name}
                    onChange={(e) => setEditingOrg({ ...editingOrg, name: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    placeholder="Enter organization description"
                    rows={3}
                    value={editingOrg.description}
                    onChange={(e) => setEditingOrg({ ...editingOrg, description: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Categories</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {AVAILABLE_CATEGORIES.map(category => (
                      <div key={category} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`edit-category-${category}`} 
                          checked={selectedCategories.includes(category)}
                          onCheckedChange={() => handleCategoryToggle(category)}
                        />
                        <label
                          htmlFor={`edit-category-${category}`}
                          className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {category}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateOrg}>Update Organization</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Invitation Dialog */}
        <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Invitation Key</DialogTitle>
              <DialogDescription>
                Share this key with users you want to invite to the organization.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="relative">
                <Input
                  value={inviteKey}
                  readOnly
                  className="pr-10"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground"
                  onClick={handleCopyInvite}
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                  <span className="sr-only">Copy</span>
                </Button>
              </div>
              
              <p className="text-sm text-muted-foreground">
                This invitation key will allow users to join the organization when signing up.
                For security, it can only be used once and expires after 30 days.
              </p>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsInviteDialogOpen(false)}>
                Close
              </Button>
              <Button onClick={handleCopyInvite}>
                {copied ? 'Copied!' : 'Copy Key'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default OrganizationManagement;
